import $ from 'jquery';
import './jquery.destroy.js';
// import { animateCSS } from '../../util/animate-css';

/**
 * @typedef {{ id: string; text: string; html?: string; selectable?: boolean; addClass?: string; removeClass?:string; attr?: any; }} AcItem
 * @typedef {(text: string, reg: RegExp) => string} OptPrepareItemHtml
 */

// ========================
// Private default settings
// ========================

const pluginName = 'mp-autocomplete';
const pluginVersion = '2.2.0';

/**
 * @typedef {defaults} AutocompleteOptions
 */
const defaults = {
	autocomplete: null,
	searchOnInit: false,
	selectFirst: false,
	setSelectionOnBlur: true,
	defaultSearchUrl: null,
	closeBlur: true,
	minChars: 1,
	delay: 266,
	sgPosition: 'absolute',
	/** @type {string|number|function} */
	sgTop: null,
	/** @type {string|number|function} */
	sgBottom: null,
	/** @type {string|number|function} */
	sgLeft: null,
	/** @type {string|number|function} */
	sgRight: null,
	/** @type {string|number|function} */
	sgOffsetLeft: 0,
	/** @type {string|number|function} */
	sgOffsetRight: 'auto',
	/** @type {string|number|function} */
	sgOffsetTop: -1,
	/** @type {string|number|function} */
	sgOffsetBottom: 'auto',
	/** @type {string|number|function} */
	sgWidth: 'fill',
	/** @type {string|number|function} */
	sgHeight: 'auto',
	/** @type {string|number|function} */
	sgWidthHeight: null,
	/** @type {string|number|function} */
	sgMaxHeight: (sgCont, input, rect) => `calc(100vh - ${Math.round(rect.bottom)}px)`,
	/** @type {function} */
	sgResizeFn: null,
	sgClassContainer: 'autocomplete-results',
	sgClassSuggestion: 'autocomplete-results__suggestion autocomplete-results__suggestion--item',
	sgClassSelected: 'autocomplete-results__suggestion--selected',
	sgClassText: 'autocomplete-results__text',
	sgClassHighlight: 'autocomplete-results__highlight',
	cache: true,
	menuClass: '',
	/** @type {any[]} */
	dataSource: [],
	enableEvents: false,
	enableEventBubbling: false,
	eventPrefix: 'MP-AC',
	bindingElements: null,
	/** @type {boolean|string|null} */
	autoBindName: null,
	bindReplaceTokens: ['{{', '}}'],

	// Using remote url
	url: null,
	remote: {
		url: null,
		data: null,
		method: null,
	},

	/** @type {(this: Autocomplete) => void} */
	onInit: $.noop,

	/** @type {(this: Autocomplete) => boolean|void} */
	onShowResults: $.noop,

	/** @type {(this: Autocomplete) => boolean|void} */
	onHideResults: $.noop,

	/** @type {(this: Autocomplete) => boolean|void} */
	onSearchFocus: $.noop,

	/** @type {(this: Autocomplete) => boolean|void} */
	onSearchBlur: $.noop,

	/** @type {(this: Autocomplete, term: string) => boolean|void} */
	onSearchQuery: $.noop,

	/** @type {(this: Autocomplete, items: any[]) => boolean|void} */
	onSearchSuggest: $.noop,

	/** @type {(this: Autocomplete, item: string | Object, term: string) => boolean|void} */
	onRenderItem: $.noop,

	/** @type {(this: Autocomplete, $item: JQuery, originalEvent: JQuery.Event) => boolean|void} */
	onSelectItem: $.noop,

	/** @type {(this: Autocomplete) => void} */
	onDestroy: $.noop,

	// Add extra eventHandlers
	// -----------------------

	/** @type {(this: Autocomplete, settings: JQuery.AjaxSettings) => boolean|void} */
	onBeforeXhr: $.noop,

	/** @type {(this: Autocomplete, xhr: JQuery.jqXHR) => boolean|void} */
	onXhrStart: $.noop,

	/** @type {(this: Autocomplete, data: any, textStatus: JQuery.Ajax.ErrorTextStatus, xhr: JQuery.jqXHR) => boolean|void} */
	onXhrSuccess: $.noop,

	/** @type {(this: Autocomplete, xhr: JQuery.jqXHR, textStatus: JQuery.Ajax.ErrorTextStatus, errorThrown: string) => boolean|void} */
	onXhrError: $.noop,

	/** @type {(this: Autocomplete, dataOrXhr: any|JQuery.jqXHR, textStatus: JQuery.Ajax.ErrorTextStatus, errorThrown: string|JQuery.jqXHR) => boolean|void} */
	onXhrComplete: $.noop,

	/**
	 * Custom xhr promise handler
	 * @type {(this: Autocomplete, term: string) => [(response: any) => any, ((error: any) => any)?, (() => any)?] }
	 */
	xhrPromiseHandler: null,

	noTriggerSearchKeys: {
		'9': 'tab',
		'13': 'enter',
		'16': 'shift',
		'17': 'ctrl',
		'18': 'alt',
		'27': 'esc',
		'33': 'page up',
		'34': 'page down',
		'35': 'end',
		'36': 'home',
		'37': 'left arrow',
		'38': 'up arrow',
		'39': 'right arrow',
		'40': 'down arrow',
		'91': 'left meta',
		'92': 'right meta',
		'93': 'select',
		'112': 'f1',
		'113': 'f2',
		'114': 'f3',
		'115': 'f4',
		'116': 'f5',
		'117': 'f6',
		'118': 'f7',
		'119': 'f8',
		'120': 'f9',
		'121': 'f10',
		'122': 'f11',
		'123': 'f12',
		'144': 'num lock',
		'145': 'scroll lock',
	},

	termReplaceRules(term) {
		// escape special characters
		term = term.replaceAll(/[$()*+./?[\\\]^{|}-]/g, '\\$&');
		const re = term ? new RegExp(`(${term.split(' ').join('|')})`, 'gi') : /(.+)/g;

		return {
			term,
			re,
		};
	},
};

const $W = $(window);
const $D = $(document);

/**
 * Autocomplete
 *
 * @class
 */
export default class Autocomplete {

	/**
	 * @param {HTMLInputElement} elem
	 * @param {AutocompleteOptions} options
	 */
	constructor(elem, options) {

		// Save the init args for initialization
		this._initArgs = [elem, options];
		this._isDestroyed = false;

		this.init(elem, options);
	}

	// ===========================
	// ===== Public methods ======
	// ===========================

	/**
	 * Plugin initializer
	 *
	 * @param {HTMLInputElement} inputElement
	 * @param {AutocompleteOptions} options
	 */
	init(inputElement, options) {

		if (!inputElement || !(inputElement.nodeType === 1) || !(inputElement.nodeName === 'INPUT')) {
			throw new TypeError('Invalid inputElement provided');
		}

		const that = this;
		const $input = $(inputElement);
		let dataSelf = $input.data(`plugin-${pluginName}`);

		// Plugin already initialized
		if (dataSelf instanceof Autocomplete) {
			// Destroy it
			dataSelf.destroy();
			dataSelf = null;
		}

		// Add reference to this instance on the data prop
		$input.data(`plugin-${pluginName}`, this);

		// Add instance to the stack
		Autocomplete.instances.push(this);

		// Get data from element
		const inputData = $input.data() || {};
		// Extend with defaults
		options = this.options = this.extendDefaults(inputData, options);
		let currentItemData = options.autocomplete || {};

		// Create the suggestions container
		const $sgCont = this.sgCreate();

		// Object references
		this.refs = {
			$input,
			$sgCont,
			bindings: [],
		};

		// var oVal = $input.val;
		// $input.val = function (...args) {
		// 	debugger;
		// 	return oVal.call($input, ...args);
		// };

		// Cache the real property
		// var oValue = Object.getOwnPropertyDescriptor(inputElement.__proto__, 'value');

		// // Replace the property with a custom one
		// Object.defineProperty(inputElement, 'value', {
		// 	configurable: true,
		// 	enumerable: true,
		// 	get: function () {
		// 		return oValue.get.call(inputElement);
		// 	},
		// 	set: function (value) {
		// 		debugger;
		// 		oValue.set.call(inputElement, value);
		// 	}
		// });

		// Last search object
		this.lastSearch = {
			/** @type {string} */
			term: null,
			/** @type {any[]|string} */
			items: [],
			/** @type {'list'|'text'} */
			// @ts-ignore
			mode: 'list',
			params: null,
		};

		// DataSource object
		this.dataSource = Array.isArray(options.dataSource) ? options.dataSource : [];

		// Query vars
		this.deferredQuery = $.Deferred().resolve([]);
		this._currentQueryState = null;
		this._currentSearchParams = null;
		this._queueChain = [];

		// Private refs
		this._sgIsHidden = true;
		this._sgTriggerResize = true;

		// Private timers
		this._timers = {
			searchDelay: undefined,
			showSg: undefined,
			hideSg: undefined,
			pasteSearch: undefined,
			clickAllow: undefined,
		};

		// Backup original input data
		this.backups = {};
		this.backups.autocompleteAttr = $input.attr('autocomplete');

		// Use the data text provided (overwrite the current value)
		if (currentItemData.text) {
			// Note: don't trigger any input event here, this is an initialization logic
			that.refs.$input.val(currentItemData.text);
		}

		const val = inputElement.value;

		// Other props
		this.cache = {};
		this.lastValue = val;
		this.last$elem = null;

		// Prevent triggering the change event until a valid selection is made
		this.preventChangeOutside = false;
		this.lastChangeValue = val;

		// Disable browser autocomplete
		if (this.backups.autocompleteAttr !== 'off') {
			$input.attr('autocomplete', `off-${Math.random().toString(32).replace('.', '-')}`);
		}

		// Add events to the input element
		this.addEvents();

		// Add auto-binding input
		if (options.autoBindName) {
			// Create the array
			if (!options.bindingElements) {
				options.bindingElements = [];
			}

			const inputName = $input.attr('name');
			// Use current input name
			const bindName = options.autoBindName === true ? inputName : options.autoBindName;

			if (bindName === inputName) {
				$input.removeAttr('name');
			}

			// Add the binding element
			options.bindingElements.push({
				element: `<input type="hidden" name="${bindName}" />`,
				attr: {
					value(data) {
						return JSON.stringify(that._normalizeItemDataSimple(data));
					},
				},
			});
		}

		// Add data bindings to elements provided
		if (options.bindingElements) {
			this.addDataBindings(options.bindingElements);
		}

		if (val) {
			currentItemData.text = val;
		}

		// We have necessary data to init bindings
		if (currentItemData.id) {
			if (!currentItemData.text) {
				currentItemData.text = val;
			}

			// Make sure we have everything we need
			currentItemData = this._normalizeItemData(currentItemData);

			// Bind data
			this.bindDataExternally(currentItemData);
		}

		// Init a full search and do a silent pre-populate action
		if (options.searchOnInit && val && !currentItemData.id) {
			this.searchStart(val).searchSuggest({ displayResults: false });
		}

		// Trigger the xhr-complete event
		if (this.trigger('init', []) !== false) {
			// Placeholder for some future code if need be
		}

		return this;
	}

	/**
	 * Re-initialize the instance
	 */
	reInit() {
		if (this._isDestroyed) {
			// @ts-ignore
			this.init(...this._initArgs);
		}
		else {
			console.warn('Cannot re-initialize and already initialized component (%o)', this);
		}

		return this;
	}

	// Cleanup data
	destroy() {

		// Trigger the destroy event (un-cancelable)
		this.trigger('destroy', []);

		// Get bindings
		const { bindings } = this.refs;

		// Remove bindings from dom
		for (const binding of bindings) {
			binding.callbacks = null;
			binding.$bindElem.remove();
			binding.$bindElem = null;
		}
		this.refs.bindings = null;

		// Abort pending queries
		this._queryAbort();

		// Clear timers
		for (const [, timer] of Object.entries(this._timers)) {
			timer && window.clearTimeout(timer);
		}

		// Detach all events
		this.removeEvents();

		// Restore the backup data
		if (this.backups.autocompleteAttr) {
			this.refs.$input.attr('autocomplete', this.backups.autocompleteAttr);
		}

		// Remove the results container
		this.refs.$sgCont.remove();

		// Remove data
		this.refs.$input.removeData(`plugin-${pluginName}`);
		this.refs.$input.removeData('autocomplete');
		// Reset the value
		this.refs.$input.val('');

		// Clean class props
		for (const propertyName of Object.getOwnPropertyNames(this)) {
			if (propertyName !== '_initArgs') {
				this[propertyName] = null;
			}
		}

		this._isDestroyed = true;

		// Remove from instance refs
		Autocomplete.instances.splice(Autocomplete.instances.indexOf(this), 1);
	}

	/**
	 * Extend default options
	 *
	 * @returns {AutocompleteOptions}
	 */
	extendDefaults(...args) {

		const obj = {};
		const def = defaults;
		for (let i = 0, l = args.length; i < l; ++i) {
			const options = args[i];
			if (options) {
				for (const s in def) {
					// Has value
					if (options[s] !== undefined) {
						obj[s] = options[s];
					}
					// Use default value
					else {
						// Only if not already set
						if (obj[s] === undefined) {
							obj[s] = def[s];
						}
					}
				}
			}
		}

		// @ts-ignore
		return obj;
	}

	/**
	 * Replace selected node with another
	 *
	 * @param {Node} targetNode
	 * @param {Array<Node>} replacements
	 *
	 * @return {void}
	 */
	replaceNode(targetNode, replacements) {

		const { parentNode } = targetNode;

		// Must have a parent
		if (parentNode && replacements && replacements.length > 0) {
			for (let i = 0, l = replacements.length; i < l; ++i) {
				parentNode.insertBefore(replacements[i], targetNode);
			}
			// eslint-disable-next-line unicorn/prefer-dom-node-remove
			parentNode.removeChild(targetNode);
		}
	}

	// Source function
	addDataBindings(dataBindings) {

		const that = this;
		const { options } = that;
		const rt = options.bindReplaceTokens;
		const { $input } = that.refs;

		if (Array.isArray(dataBindings)) {

			const reBindReplace = new RegExp(`(${rt[0]}([\\s\\S]+)${rt[1]})`);

			for (let i = 0, l = dataBindings.length; i < l; ++i) {
				const currentBind = dataBindings[i];
				let $bindElem;

				if (currentBind.selector) {
					$bindElem = $(currentBind.selector);
				}
				else if (currentBind.element) {
					$bindElem = $(currentBind.element);
				}

				// No element found
				if (!$bindElem || $bindElem.length === 0) {
					continue;
				}

				const callbacks = [];
				let expr;
				let match;

				// Have attribute bindings
				if (currentBind.attr) {
					// var attrList = $bindElem[0].attributes;
					const attrList = currentBind.attr;

					// Look in attributes list
					for (const [k, attrVal] of Object.entries(attrList)) {
						expr = attrVal;
						if (typeof attrVal === 'string') {
							match = reBindReplace.exec(attrVal);
							if (match) {
								// eslint-disable-next-line unicorn/no-unreadable-array-destructuring
								[, , expr] = match;
							}
						}
						// Remove the binding template
						$bindElem.attr(k, '');
						// Add callback for this attribute value
						callbacks.push({
							args: [k],
							expr,
							fn(attrName, expr, data) {
								const val = (expr.call ? expr.call(this, data, attrName) : eval(expr)) || '';
								this.attr(attrName, val);
							},
						});
					}
				}

				// Have content binding
				if (currentBind.content) {
					expr = currentBind.content;
					if (typeof expr === 'string') {
						match = reBindReplace.exec(expr);
						if (match) {
							// eslint-disable-next-line unicorn/no-unreadable-array-destructuring
							[, , expr] = match;
						}
					}
					// Remove contents
					$bindElem.html('');
					// Add callback for this attribute value
					callbacks.push({
						args: [],
						expr,
						fn(expr, data) {
							const val = (expr.call ? expr.call(this, data) : eval(expr)) || '';
							this.html(val);
						},
					});
				}

				// Add binding
				that.refs.bindings.push({ $bindElem, callbacks });

				// Add the binding elem to the dom if not already added
				if ($bindElem.parent().length === 0) {
					$bindElem.insertAfter($input);
				}
			}
		}

		return this;
	}

	// Source function
	bindDataExternally(itemData) {

		const that = this;
		// Trigger one way data binding
		const { bindings } = that.refs;
		const len = bindings.length;

		if (len) {
			for (let i = 0; i < len; ++i) {
				const binding = bindings[i];
				const { $bindElem } = binding;
				const { callbacks } = binding;
				const numCb = callbacks.length;
				if (numCb) {
					// Trigger callbacks
					for (let j = 0; j < numCb; ++j) {
						const cb = callbacks[j];
						// eslint-disable-next-line unicorn/prefer-spread
						cb.fn.apply($bindElem, cb.args.concat(cb.expr, itemData));
					}
				}
			}
		}

		return this;
	}

	/**
	 * Return the item selector
	 */
	getItemSelector() {
		const { options } = this;
		const itemClass = options.sgClassSuggestion;
		// const itemSelectedClass = options.sgClassSelected;
		const itemSelector = itemClass ? (`.${itemClass.split(' ').join('.')}`) : '[data-search-results-item]';
		// const itemSelector = '[data-search-results-item]';
		// const itemIsSelectedSelector = itemSelectedClass ? ('.' + itemSelectedClass.split(' ').join('.')) : '[data-search-results-item-selected]';
		const itemIsSelectedSelector = '[data-search-results-item-selected]';

		return { itemSelector, itemIsSelectedSelector };
	}

	/**
	 * Return the text selector
	 */
	getTextSelector() {
		const { options } = this;
		const textClass = options.sgClassText;
		const textSelector = textClass ? (`.${textClass.split(' ').join('.')}`) : '[data-search-results-text]';

		return textSelector;
	}

	/**
	 * @param {string} term
	 * @returns {{ [key: string]: string }}
	 */
	getSearchParams(term) {

		const that = this;
		const { data } = that.options.remote;
		let params;

		if (data) {
			params = typeof data === 'function' ? data(term) : data;
		}
		else {
			params = { q: term };
		}

		return params;
	}

	/**
	 * Start a search
	 *
	 * @param  {string} term
	 * @param  {{skipCache?: boolean, delay?: number, suggestOnComplete?: boolean}=} opt
	 *
	 * @return {Autocomplete}
	 */
	searchStart(term, opt) {

		opt = opt || {};
		const that = this;
		const { lastSearch } = that;
		const currentParams = $.param(that.getSearchParams(term));
		// Save in temporary cache until setLastResult() is called
		this._currentSearchParams = currentParams;

		// No need to redo last search
		if (lastSearch && (lastSearch.term === term) && lastSearch.params === currentParams) {

			// Enabled auto suggestions display
			if (opt.suggestOnComplete) {
				that.searchSuggest();
			}

			return this;
		}

		// Make sure there are no pending queries
		that._queryAbort();
		that.lastValue = term;

		// Suggest from cache first
		if (!opt.skipCache && that.options.cache) {

			// Get result from cache
			if (that.cache[currentParams]) {

				// Go straight to suggestions display
				that.setLastResult(term, that.cache[currentParams])/* .searchSuggest() */;

				// Enabled auto suggestions display
				if (opt.suggestOnComplete) {
					that.searchSuggest();
				}

				return this;
			}

			// There was nothing in cache for the current term
			// Look for prev cache entries
			if (term) {
				// This approach is buggy and must be disabled
				// E.g.: Springfield missssss
				// E.g.: missss
				/* for (var i = that.options.minChars, l = term.length; i < l && i >= that.options.minChars; ++i) {
					var part = term.slice(0, l - i);
					console.log('Prev cache entry: ', part);
					// Check prev entries
					if (that.cache[part] && !that.cache[part].length) {
						return this;
					}
				} */
			}
		}

		// Process the search
		that.searchQuery(term, opt);

		return this;
	}

	/**
	 * Process the started search
	 *
	 * @param  {string} term
	 * @param  {{delay?: number, suggestOnComplete?: boolean}} opt
	 *
	 * @return {Autocomplete}
	 */
	searchQuery(term, opt) {

		const that = this;

		// Make sure there are no pending queries
		that._queryAbort();

		// Not cancelled
		if (that.trigger('search-query', [term]) !== false) {

			// Set new lastSearch data
			that.lastSearch.term = term;
			that.lastSearch.items = [];
			that.lastSearch.mode = 'list';
			that.lastSearch.params = that._currentSearchParams;
			// Set new query state
			that._currentQueryState = 'delayed';

			// Add delay
			that._timers.searchDelay = window.setTimeout(() => {
				// Set new query state
				that._currentQueryState = 'pending';
				// Start query (and keep a reference of the deferred object)
				// @ts-ignore
				that.deferredQuery = that._queryStart(term, opt);
			}, opt.delay);
		}
		// Event is cancelled
		else {
			// Use a deferred object
			that.deferredQuery = $.Deferred().reject(false);
		}

		return this;
	}

	/**
	 * Store the search result for different processing
	 *
	 * @param  {string} term
	 * @param  {Array<(string | Object)>} items
	 *
	 * @return {Autocomplete}
	 */
	setLastResult(term, items) {

		const that = this;
		const { lastSearch } = that;
		const resultIsArray = Array.isArray(items);

		if (items && !resultIsArray && typeof items !== 'string') {
			console.warn(pluginName, 'Expecting autocomplete dataSource to be array or string:', items);
			items = Array.prototype.slice.call(items);
		}

		const params = that._currentSearchParams || $.param(that.getSearchParams(term));
		that._currentSearchParams = null;

		// Store value in cache
		that.cache[params] = items;

		// Store the last search result
		lastSearch.term = term;
		lastSearch.items = items;
		lastSearch.mode = resultIsArray ? 'list' : 'text';
		lastSearch.params = params;

		return this;
	}

	/**
	 * Clear last search result
	 *
	 * @param {boolean=} clearFromCache
	 */
	clearLastSearch(clearFromCache) {

		const that = this;
		const { lastSearch } = that;

		if (lastSearch) {

			if (clearFromCache === true) {
				that.clearCache(lastSearch.term);
			}

			lastSearch.term = null;
			lastSearch.items = [];
			lastSearch.mode = 'list';
			lastSearch.params = null;
		}

		return this;
	}

	/**
	 * Clear search cache
	 *
	 * @param {string=} term
	 */
	clearCache(term) {

		if ((term != null) && this.cache[term]) {
			delete this.cache[term];
		}
		else {
			this.cache = {};
		}

		return this;
	}

	/**
	 * @param {{ displayResults?: boolean; selectFirst?: boolean; checkAllowInvalid?: boolean; delay?: number; }} opt
	 */
	triggerAutocomplete(opt) {

		opt = opt || {};
		const that = this;
		const globalOpt = that.options;
		const { $input } = that.refs;
		const inputElement = $input[0];
		const val = inputElement.value;
		// const item = $input.data('autocomplete');

		// // Item is already selected
		// // This is no longer needed - to be removed at diff check
		// if (item && item.text == val) {
		// 	console.log('Hmmm (item: %o, item.text: %o, val: %o)', item, item.text, val);
		// 	return this;
		// }
		// else if (opt.checkAllowInvalid === true) {
		// 	console.log('checkAllowInvalid');
		// }

		// Min char threshold bypassed
		if (val.length >= globalOpt.minChars) {

			const suggestOpt = {};
			suggestOpt.displayResults = ('displayResults' in opt) ? opt.displayResults : true;
			suggestOpt.selectFirst = ('selectFirst' in opt) ? opt.selectFirst : globalOpt.selectFirst;

			// eslint-disable-next-line eqeqeq
			if (val != that.lastValue) {

				const searchOpt = {};
				searchOpt.delay = ('delay' in opt) ? opt.delay : globalOpt.delay;

				that.lastValue = val;
				that.searchStart(val, searchOpt);

				// Display suggestions
				that.searchSuggest(suggestOpt);
			}
		}
		// Min char limit - discard values
		else {
			// Abort pending queries
			that._queryAbort();

			// eslint-disable-next-line eqeqeq
			if (val != that.lastValue) {
				that._triggerSelectItemChange(val, { id: null, text: val }, { hideSuggestions: true });
			}
		}

		return that;
	}

	/**
	 * Search response callback (parse the response items into the suggestions container)
	 *
	 * @param  {{displayResults?: boolean; selectFirst?: boolean; prepareItemHtml?: OptPrepareItemHtml;}=} opt
	 * @param  {{term?: string; items?: any[]|string; mode?: 'list'|'text'; params?: string;}=} forcedLastSearch
	 *
	 * @return {Autocomplete}
	 */
	searchSuggest(opt, forcedLastSearch) {

		const that = this;

		// Add to deferred queue if there is an active query
		if (that._currentQueryState !== null) {
			that._addQueueChain(that.searchSuggest, [opt, forcedLastSearch]);
			return this;
		}

		// console.log('Called searchSuggest');
		opt = opt || {};
		opt.displayResults = (opt.displayResults == null) ? true : Boolean(opt.displayResults);

		/** @type {JQuery<HTMLElement>} */ // @ts-ignore
		let { $sgCont } = that.refs;
		const sgLastSearch = $sgCont.data('lastSearch') || {};
		// const inputData = this.refs.$input.data('autocomplete') || {};

		const lastSearch = forcedLastSearch || that.lastSearch;
		const { term, items, mode, params } = lastSearch;

		const showResults = () => {
			that._sgTriggerResize = true;

			// Nothing to display
			if (items.length === 0) {
				that.sgHide();
				return;
			}

			// Select first item
			if (opt.selectFirst) {
				that.selectItem(0, { hideSuggestions: false });
			}

			// Don't show suggestions if less than 1 items
			if ((mode === 'list') && (items.length <= 1) && (that.refs.$input.val() === that.getItemAtPos(0).data('text'))) {
				return;
			}

			// Show results only if element still has focus
			if (document.activeElement !== that.refs.$input[0]) {
				return;
			}

			// Display the results container
			if (opt.displayResults) {
				that.sgShow();
			}
		};

		// No need to re-parse the results (the container might be different)
		if (sgLastSearch.term && items && (sgLastSearch.term === term) && (sgLastSearch.mode === mode) && (sgLastSearch.params === params)) {
			// Show the results container
			showResults();
			return this;
		}

		// Not cancelled
		if (that.trigger('search-suggest', [items, mode]) !== false) {

			// Process items from result set
			if (items && items.length > 0) {

				// Array of items
				if (mode === 'list') {

					/** @type {JQuery<HTMLElement>} */
					let $sgItems = $();

					// Render each item
					for (let i = 0, l = items.length; i < l; ++i) {
						const $item = that.renderItem(items[i], term, mode, opt);
						if ($item !== false) {
							$sgItems = $sgItems.add($item);
						}
					}

					// @ts-ignore
					$sgCont.html($sgItems);
				}
				// Items string (pre-rendered)
				else {

					const { options } = that;
					const $markup = $(items);
					let $newCont = $markup.filter('[data-search-results]');
					if ($newCont.length === 0) {
						$newCont = $markup.find('[data-search-results]');
					}
					if ($newCont.length > 0) {
						// Replace with new container
						$sgCont.replaceWith($newCont);
						// Remove events from the container
						that.sgRemoveEvents();
						// Replace all refs
						$sgCont = that.refs.$sgCont = $newCont;

						// Make sure container is hidden
						if (that._sgIsHidden === true) {
							$sgCont.hide();
						}

						// Make sure there are no white-spaces
						if ($sgCont[0].textContent.trim() === '') {
							$sgCont.empty();
						}

						// Make sure all events are set
						that.sgAddEvents();
						// Make sure the position is set correctly
						that.sgResize({ delay: 0 });
					}
					else {
						// @ts-ignore
						$sgCont.html(items);
					}

					const termReplaceRules = options.termReplaceRules(term);
					const textSelector = that.getTextSelector();

					$sgCont.find(textSelector).each((_i, elem) => {
						$(elem).replaceWith(that._prepareItemHtml(elem.innerHTML, termReplaceRules.re));
					});
				}

				// Set last search data to the container (the container might be different)
				$sgCont.data('lastSearch', { term, mode, params });

				// Show the results container
				showResults();
			}
			// Nothing to display
			else {

				// Set last search data to the container (the container might be different)
				$sgCont.data('lastSearch', { term, mode, params });

				that.sgHide();
			}
		}

		return this;
	}

	/**
	 * @param  {string|Object} item
	 * @param  {string} searchTerm
	 * @param  {'list'|'text'} mode
	 * @param  {{ prepareItemHtml?: OptPrepareItemHtml; }} opt
	 *
	 * @return {(JQuery | false)}
	 */
	renderItem(item, searchTerm, mode, opt) {

		// Not cancelled
		if (this.trigger('render-item', [item, searchTerm, mode]) !== false) {

			const { options } = this;
			const { sgClassSuggestion, termReplaceRules } = options;
			const { term, re } = termReplaceRules(searchTerm);
			const $item = $('<div />');

			// Make sure we have everything we need in itemData
			const itemData = this._normalizeItemData(item);
			opt = opt || {};

			// Only selectable items have the correct class/attr
			if (itemData.selectable === true) {
				$item.addClass(sgClassSuggestion).attr('data-search-results-item', '');
			}
			// Item is not selectable
			else {
				const cls = (sgClassSuggestion || '').split(' ');
				// Only add the first class if many
				if (cls.length > 1) {
					$item.addClass(cls[0]);
				}
			}

			$item.attr(itemData.attr);
			if (itemData.addClass) {
				$item.addClass(itemData.addClass);
			}
			if (itemData.removeClass) {
				$item.removeClass(itemData.removeClass);
			}

			const itemHtml = itemData.selectable ? (opt.prepareItemHtml ? opt.prepareItemHtml(item.html || item.text, re) : this._prepareItemHtml(item.html || item.text, re)) : (item.html || item.text);

			return $item
				.data(itemData)
				.html(itemHtml);
		}

		return false;
	}

	/**
	 * Return the item at specified index in list
	 *
	 * @param  {number} itemIndex
	 *
	 * @return {JQuery}
	 */
	getItemAtPos(itemIndex) {

		const { itemSelector } = this.getItemSelector();
		const $item = this.refs.$sgCont.find(itemSelector).eq(Number(itemIndex));

		return $item;
	}

	/**
	 * Return the currently selected item
	 *
	 * @return {JQuery}
	 */
	getItemSelected() {

		const { itemIsSelectedSelector } = this.getItemSelector();
		const $item = this.refs.$sgCont.find(itemIsSelectedSelector).first();

		return $item;
	}

	/**
	 * Add the selected item to the input value
	 *
	 * @param  {JQuery|number} indexOrObj
	 * @param  {{hideSuggestions?: boolean; allowChangeEvent?: boolean;}=} opt
	 * @param  {JQuery.Event=} originalEvent
	 *
	 * @return {Autocomplete}
	 */
	selectItem(indexOrObj, opt = {}, originalEvent = null) {

		// Add to deferred queue if there is an active query
		if (this._currentQueryState !== null) {
			this._addQueueChain(this.selectItem, [indexOrObj, opt, originalEvent]);
			return this;
		}

		/** @type {JQuery} */ // @ts-ignore
		const $item = (!Number.isNaN(Number(indexOrObj)))
			// Try to find the item by index
			? this.getItemAtPos(Number(indexOrObj))
			// Already jquery
			: indexOrObj;

		// No item found
		if (!$item || $item.length === 0) {
			return this;
		}
		// Item is already selected
		else if (this.last$elem && ($item[0] === this.last$elem[0])) {
			return this;
		}

		// Set selected item
		this.last$elem = $item;

		// Not cancelled
		if (this.trigger('select-item', [$item, originalEvent]) !== false) {

			// Stop event propagation
			if (originalEvent) {
				// Make sure form is not submitted or link is not clicked
				originalEvent.preventDefault();
				originalEvent.stopPropagation();
				originalEvent.stopImmediatePropagation();
			}

			// Allow outside change events to trigger
			if (opt.allowChangeEvent === true) {
				this.preventChangeOutside = false;
			}

			const itemData = $item.data();
			// Make the selection
			this._triggerSelectItemChange(itemData.text, itemData, opt);
		}

		return this;
	}

	/**
	 * Item renderer function
	 *
	 * @param {string} eName
	 * @param {any[]} dataArr
	 */
	trigger(eName, dataArr) {

		const { options } = this;
		const { eventPrefix } = options;

		// Get handler from options
		let handler = options[$.camelCase(`on-${eName}`)];

		// We have a handler defined
		if (handler && handler !== $.noop) {

			// Convert handler to eval function
			if (typeof handler === 'string') {
				const code = handler;
				handler = function(arg1, arg2, arg3, arg4) {
					eval(code);
				};
			}

			// Call the handler from options
			const retVal = handler.apply(this, dataArr);

			// Prevented default action
			if (retVal === false) {
				return false;
			}
		}

		// Events are enabled
		if (options.enableEvents) {

			// Create the event object
			const e = new $.Event(`${eventPrefix}:${eName}`);
			const { $input } = this.refs;
			let retVal;

			// Use natural event bubbling
			if (options.enableEventBubbling) {
				// eslint-disable-next-line unicorn/prefer-spread
				$input.trigger(e, [this].concat(dataArr));
			}
			// Execute only the first handler on the input element
			else {
				// eslint-disable-next-line unicorn/prefer-spread
				retVal = $input.triggerHandler(e, [this].concat(dataArr));

				// Prevented default action
				if (retVal === false) {
					e.preventDefault();
				}
			}

			// Prevented default action
			if ((retVal === false) || e.isDefaultPrevented()) {
				// console.info('Event is defaultPrevented: ', e);
				return false;
			}
		}

		return true;
	}

	/**
	 * Create the suggestions container
	 */
	sgCreate() {
		const { options } = this;
		this._firstSgCreate = true;

		return $(`<div class="${options.sgClassContainer}" data-search-results />`)
			.addClass(options.menuClass)
			.hide()
			.appendTo(document.body);
	}

	/**
	 * Add events to the suggestions container (method can be called multiple times)
	 */
	sgAddEvents() {

		const that = this;
		const { options } = this;
		const { refs } = this;
		// The sgCont must be re-referenced every time, because it is re-written
		const { $sgCont } = refs;
		const { itemSelector, itemIsSelectedSelector } = this.getItemSelector();
		const itemIsHoveredAttr = 'data-search-results-item-hovered';
		const itemIsHoveredSelector = `[${itemIsHoveredAttr}]`;

		// Attach all sg events
		$sgCont.on({

			// Mouse enter: change selected class
			[`mouseenter.${pluginName}`]() {
				// The sgCont must be re-referenced every time, because it is re-written
				const { $sgCont } = refs;
				const $this = $(this);

				if ($this.is(itemSelector)) {
					const $sel = $sgCont.find(itemIsHoveredSelector).not(itemIsSelectedSelector);
					if ($sel.length > 0) {
						$sel.removeClass(options.sgClassSelected).removeAttr(itemIsHoveredAttr);
					}
					$this.addClass(options.sgClassSelected).attr(itemIsHoveredAttr, '');
				}
			},

			// Mouse leave: change selected class
			[`mouseleave.${pluginName}`]() {
				// The sgCont must be re-referenced every time, because it is re-written
				const { $sgCont } = refs;
				const $sel = $sgCont.find(itemIsHoveredSelector).not(itemIsSelectedSelector);
				if ($sel.length > 0) {
					$sel.removeClass(options.sgClassSelected).removeAttr(itemIsHoveredAttr);
				}
			},

			// Mouse down (click): handle item selection
			// Note: we use the mousedown handler because the 'mousedown' event triggers before the 'blur' event
			[`mousedown.${pluginName}`](e) {
				that.selectItem($(this), { allowChangeEvent: true }, e);
			},

			// Mouse down (click): handle item selection
			// Note: this actually never triggers because the blur event triggers before the click event and the selection container is instantly hidden to allow proper 'change' propagation
			// This is only set-up as a fallback in case something changes in the future
			[`click.${pluginName}`](e) {
				that.selectItem($(this), { allowChangeEvent: true }, e);
			},

		}, itemSelector);
	}

	/**
	 * Remove events to the suggestions container (method can be called multiple times)
	 */
	sgRemoveEvents() {
		// Remove all namespaced events on the sgCont element
		this.refs.$sgCont.off(`.${pluginName}`);
	}

	/**
	 * Resize the suggestions list
	 *
	 * @param {{ delay?: number; }=} opt
	 * @param {Function=} cb
	 */
	sgResize(opt, cb) {

		const that = this;
		const { options } = that;
		const { _timers } = that;

		opt = opt || { delay: 0 };

		if (opt && opt.delay > 0) {
			if (_timers.resizeSg) {
				window.clearTimeout(_timers.resizeSg);
			}
			_timers.resizeSg = window.setTimeout(() => {
				delete opt.delay;
				that.sgResize(opt, cb);
			}, opt.delay);

			return this;
		}

		const { refs } = that;
		const inputElement = refs.$input[0];
		const sgContElement = refs.$sgCont[0];

		if (sgContElement && inputElement && sgContElement.parentElement) {

			// Apply custom position
			sgContElement.style.position = options.sgPosition || '';

			// Input rect
			// $input.addClass('__trigger__repaint__').removeClass('__trigger__repaint__');
			const rect = inputElement.getBoundingClientRect();

			// Viewport (window.innerWidth/window.innerHeight includes the scroll-bar width/height)
			// var vw = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
			// var vh = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
			// Scroll bar width/height
			// var sw = window.outerWidth - vw;
			// var sh = window.outerHeight - vh;

			const SCR = document.scrollingElement || document.documentElement;
			const vw = SCR.clientWidth;
			const vh = SCR.clientHeight;

			const sgWidth = (typeof options.sgWidth === 'function') ? options.sgWidth.call(this, sgContElement, inputElement, rect) : options.sgWidth;
			if (sgWidth === 'fill') {
				sgContElement.style.width = `${Math.round(rect.right - rect.left)}px`; // outerWidth
			}
			else if (sgWidth) {
				sgContElement.style.width = String(sgWidth);
			}

			const sgMaxWidth = (typeof options.sgMaxWidth === 'function') ? options.sgMaxWidth.call(this, sgContElement, inputElement, rect) : options.sgMaxWidth;
			if (sgMaxWidth) {
				sgContElement.style.maxWidth = String(sgMaxWidth);
			}

			const sgHeight = (typeof options.sgHeight === 'function') ? options.sgHeight.call(this, sgContElement, inputElement, rect) : options.sgHeight;
			if (sgHeight) {
				sgContElement.style.height = String(sgHeight);
			}

			const sgMaxHeight = (typeof options.sgMaxHeight === 'function') ? options.sgMaxHeight.call(this, sgContElement, inputElement, rect) : options.sgMaxHeight;
			if (sgMaxHeight) {
				sgContElement.style.maxHeight = String(sgMaxHeight);
			}

			const sgLeft = (typeof options.sgLeft === 'function') ? options.sgLeft.call(this, sgContElement, inputElement, rect) : options.sgLeft;
			const sgOffsetLeft = (typeof options.sgOffsetLeft === 'function') ? options.sgOffsetLeft.call(this, sgContElement, inputElement, rect) : options.sgOffsetLeft;
			// Fixed left placement
			if (sgLeft != null) {
				sgContElement.style.left = (sgLeft === 'auto') ? 'auto' : `${Math.round(Number(sgLeft))}px`;
			}
			// Relative left placement
			else if (sgOffsetLeft != null) {
				sgContElement.style.left = `${Math.round(rect.left + (/* window.pageXOffset ||  */SCR.scrollLeft) + Number(sgOffsetLeft))}px`;
			}

			const sgRight = (typeof options.sgRight === 'function') ? options.sgRight.call(this, sgContElement, inputElement, rect) : options.sgRight;
			const sgOffsetRight = (typeof options.sgOffsetRight === 'function') ? options.sgOffsetRight.call(this, sgContElement, inputElement, rect) : options.sgOffsetRight;
			// Fixed right placement
			if (sgRight != null) {
				sgContElement.style.right = (sgRight === 'auto') ? 'auto' : `${Math.round(Number(sgRight))}px`;
			}
			// Relative right placement
			else if (sgOffsetRight != null) {
				sgContElement.style.right = `${Math.round(vw - rect.right - (/* window.pageXOffset ||  */SCR.scrollLeft) + Number(sgOffsetRight))}px`;
			}

			const sgTop = (typeof options.sgTop === 'function') ? options.sgTop.call(this, sgContElement, inputElement, rect) : options.sgTop;
			const sgOffsetTop = (typeof options.sgOffsetTop === 'function') ? options.sgOffsetTop.call(this, sgContElement, inputElement, rect) : options.sgOffsetTop;
			// Fixed top placement
			if (sgTop != null) {
				sgContElement.style.top = (sgTop === 'auto') ? 'auto' : `${Math.round(Number(sgTop))}px`;
			}
			// Relative top placement
			else if (sgOffsetTop != null) {
				sgContElement.style.top = `${Math.round(rect.bottom + (/* window.pageYOffset ||  */SCR.scrollTop) + Number(sgOffsetTop))}px`;
			}

			const sgBottom = (typeof options.sgBottom === 'function') ? options.sgBottom.call(this, sgContElement, inputElement, rect) : options.sgBottom;
			const sgOffsetBottom = (typeof options.sgOffsetBottom === 'function') ? options.sgOffsetBottom.call(this, sgContElement, inputElement, rect) : options.sgOffsetBottom;
			// Fixed bottom placement
			if (sgBottom != null) {
				sgContElement.style.bottom = (sgBottom === 'auto') ? 'auto' : `${Math.round(Number(sgBottom))}px`;
			}
			// Relative bottom placement
			else if (sgOffsetBottom != null) {
				sgContElement.style.bottom = `${Math.round(vh - rect.bottom + rect.height - (/* window.pageYOffset ||  */SCR.scrollTop) + Number(sgOffsetBottom))}px`;
			}

			// Adjust min-width
			const sgResizeFn = (typeof options.sgResizeFn === 'function')
				? options.sgResizeFn
				: () => {
					sgContElement.style.minWidth = `${Math.round(rect.right - rect.left)}px`;
				};
			sgResizeFn.call(this, sgContElement, inputElement, rect);
		}

		// Force a css reflow
		// eslint-disable-next-line no-unused-expressions, @babel/no-unused-expressions
		sgContElement.offsetHeight;

		// Do another forced resize if this is the first time
		// To mitigate a bug where the position of the sg is slightly off
		// if (this._firstSgCreate) {
		// 	this._firstSgCreate = false;
		// 	this.sgResize({ delay: 0 });
		// }

		cb && cb.call(this);

		return this;
	}

	/**
	 * Show the suggestions list
	 *
	 * @param {{ delay?: number; resizeDelay?: number; force?: boolean; }=} opt
	 * @param {Function=} cb
	 */
	sgShow(opt, cb) {

		// The sgContainer is already visible
		if (this._sgIsHidden === false) {
			return this;
		}

		// Add to deferred queue if there is an active query
		if ((this._currentQueryState !== null) && (opt.force !== true)) {
			this._addQueueChain(this.sgShow, [opt, cb]);
			return this;
		}

		const { _timers } = this;
		const { $sgCont } = this.refs;
		opt = opt || { delay: 0 };

		// Container not visible
		window.clearTimeout(_timers.showSg);
		window.clearTimeout(_timers.hideSg);

		const showHandler = () => {

			let canShow = (opt.force === true) || ($sgCont.children().length > 0);

			// Trigger the show event
			if (canShow && (this.trigger('show-results', []) === false)) {
				canShow = false;
			}

			// Trigger only if there is something to display
			if (canShow) {

				// With animation
				// Note: the animation adds a flicker effect because the container shifts layout between prev results <-> next results ... odd
				// animateCSS($sgCont[0], 'fadeIn', 'faster');

				// Force the container offscreen in the top left direction (where the layout is not affected - scrollbars don't appear)
				$sgCont.css({ top: '-10000px', left: '-10000px' });

				// Display
				$sgCont.show();
				this._sgIsHidden = false;

				// Trigger resize only once
				if (this._sgTriggerResize) {
					this._sgTriggerResize = false;
					this.sgResize({ delay: opt.resizeDelay == null ? 50 : opt.resizeDelay }, cb);
				}
				else {
					cb && cb.call(this);
				}
			}
		};

		// Show with delay
		if (opt.delay > 0) {
			_timers.showSg = window.setTimeout(showHandler, opt.delay);
		}
		// Show instantly
		else {
			showHandler();
		}

		return this;
	}

	/**
	 * Hide the suggestions list
	 *
	 * @param {{ delay?: number; }=} opt
	 * @param {Function=} cb
	 */
	sgHide(opt, cb) {

		if (this._sgIsHidden === true) {
			return this;
		}

		const { _timers } = this;
		const { $sgCont } = this.refs;
		opt = opt || {};

		window.clearTimeout(_timers.showSg);
		window.clearTimeout(_timers.hideSg);

		const hideHandler = () => {
			// Trigger the hide event
			if (this.trigger('hide-results', []) !== false) {
				$sgCont.hide();
				this._sgIsHidden = true;

				// Clear results selection
				this.getItemSelected().removeClass(this.options.sgClassSelected).removeAttr('data-search-results-item-selected');

				cb && cb.call(this);
			}
		};

		// Hide with delay
		if (opt.delay > 0) {
			_timers.hideSg = window.setTimeout(hideHandler, opt.delay);
		}
		// Show instantly
		else {
			hideHandler();
		}

		return this;
	}

	/**
	 * Update the suggestions list
	 *
	 * @param {JQuery} $item
	 * @param {'up'|'down'} dir
	 */
	sgSetScrollTop($item, dir = 'down') {

		const sgContElement = this.refs.$sgCont[0];

		if ($item && $item.length > 0) {

			const item = $item[0];
			const top = item.offsetTop;
			const height = item.offsetHeight;
			const contHeight = sgContElement.offsetHeight;
			const contPos = sgContElement.scrollTop;

			// Begin scrolling down only when item is at the edge
			if (dir === 'down') {
				// Going straight down OR reached the top while still dir is down
				if ((top + height > contHeight + contPos) || (contPos > top)) {
					sgContElement.scrollTop = top - contHeight + height;
				}
			}
			// Begin scrolling up only when item is at the edge
			else {
				// Going straight up OR reached the bottom while still dir is down
				if ((top < contPos) || (top > contHeight + contPos)) {
					sgContElement.scrollTop = top;
				}
			}
		}

		return this;
	}

	/**
	 * Check if element is in view
	 *
	 * @param {HTMLElement} container
	 * @param {HTMLElement} element
	 * @param {boolean} fullyInView
	 */
	isElementInView(container, element, fullyInView) {

		const containerScroll = container.scrollTop;
		const containerHeight = container.offsetHeight;
		const elementTop = element.offsetTop;
		const wholeElemSize = (fullyInView === true) ? element.offsetHeight : 0;

		if (
			// Check top
			((elementTop + wholeElemSize) > containerScroll) &&
			// Check bottom
			((elementTop + wholeElemSize) < (containerScroll + containerHeight))
		) {
			return true;
		}

		return false;
	}

	/**
	 * Add events to the input element
	 */
	addEvents() {

		const that = this;
		const { options } = that;
		const { refs } = that;
		const { $input } = refs;
		const inputElement = $input[0];
		const { itemSelector, itemIsSelectedSelector } = that.getItemSelector();

		// const val = $input.val;
		// $input.val = function () {
		// 	debugger;
		// 	return val.apply($input, [...arguments]);
		// };

		// setTimeout(() => {
		// 	var handler = (e) => { console.trace('%o event triggered with value: %o', e.type, e.currentTarget.value); };
		// 	$input.on({ change: handler, blur: handler });
		// }, 500);

		// Init the events object
		let focusTriggered = false;
		const focusHandler = function() {

			// No change event is taken into consideration while the focus handler/logic is active (until the blur event is triggered)
			that.preventChangeOutside = true;

			// The sgCont must be re-referenced every time, because it is re-written
			// const $sgCont = refs.$sgCont;

			// Only if allowed to display
			if (inputElement.value.length >= options.minChars) {

				// Display the search results
				that.searchStart(inputElement.value, { delay: 0, suggestOnComplete: true });
			}
		};

		// Attach all input events
		// Better optimization if all are declared at once in an object literal
		$input.on({

			// Custom reload event
			[`mp-widget-reload.${pluginName}`]() {
				that.destroy();
				that.reInit();
			},

			// Cleanup: call the destructor when the element is no more
			[`destroy.${pluginName}`]() {
				that.destroy();
			},

			// Focus handler: for starting the search if options permit it
			[`focus.${pluginName}`]() {

				const { _timers } = that;
				focusTriggered = true;

				// Chrome is stubborn with it's autocomplete, we have to change the name every time
				// $input.attr('autocomplete', 'custom-' + Math.random().toString(36).replace('.', ''));
				// $input.attr('autocomplete', 'off');

				if (that.trigger('search-focus', []) !== false) {
					focusHandler();
				}

				// Reset the focusTriggered flag immediately
				window.clearTimeout(_timers.clickAllow);
				_timers.clickAllow = window.setTimeout(() => {
					focusTriggered = false;
				}, 500);
			},

			// Click handler: fallback for the focus handler
			[`click.${pluginName}`]() {
				if (that.trigger('search-focus', []) !== false && !focusTriggered) {
					focusHandler();
				}
			},

			// Blur handler: for committing the selection
			[`blur.${pluginName}`]() {

				// Event prevented
				if (that.trigger('search-blur', []) === false) {
					return;
				}

				// ------ Old deprecated code --------
				/*
				// The sgCont must be re-referenced every time, because it is re-written
				const $sgCont = refs.$sgCont;
				// const lastSearch = this.lastSearch;

				// Search results are not hovered - close the results window
				// TODO: Not sure this condition is really necessary, to keep the results window open while hovered
				if (!$sgCont.find('div:hover').length) {

					// Results are visible and input is focused
					if (this._sgIsHidden === false) {

						// Set current selection
						if (options.setSelectionOnBlur) {
							const $selectedItem = this.getItemSelected();
							if ($selectedItem.length > 0) {
								this.selectItem($selectedItem, { hideSuggestions: false });
							}
						}
						else {
							// Restore last val
							$input.val(this.lastValue).trigger('input.' + pluginName);
						}

						// Hide the results
						// Note: VERY important here that the hide is instant, so we can enable the native change event below
						this.sgHide({ delay: 0 });

						// It's time to allow the change event to trigger, because the results window is no longer visible which means the plugin logic has ended
						// Allow outside change events to trigger
						this.preventChangeOutside = false;

						if (this.lastChangeValue !== inputElement.value) {
							$input.trigger('change', [{ by: 'autocomplete-native', from: 'blurEvent' }]);
						}
					}
				}
				else {
					// It's time to allow the change event to trigger, because the results window is no longer visible which means the plugin logic has ended
					// Allow outside change events to trigger
					this.preventChangeOutside = false;

					if (this.lastChangeValue !== inputElement.value) {
						$input.trigger('change', [{ by: 'autocomplete-native', from: 'blurEvent' }]);
					}
				}
				 */
				// -----------------------------

				// Results are visible and input is focused
				if (that._sgIsHidden === false) {

					// Set current selection
					if (options.setSelectionOnBlur) {
						const $selectedItem = that.getItemSelected();
						if ($selectedItem.length > 0) {
							that.selectItem($selectedItem, { hideSuggestions: false });
						}
					}
					else {
						// Restore last val
						$input.val(that.lastValue).trigger(`input.${pluginName}`);
					}

					// Hide the results
					// Note: VERY important here that the hide is instant, so we can enable the native change event below
					that.sgHide({ delay: 0 });
				}

				// It's time to allow the change event to trigger, because the results window is no longer visible which means the plugin logic has ended
				// Allow outside change events to trigger
				that.preventChangeOutside = false;

				if (that.lastChangeValue !== inputElement.value) {
					$input.trigger('change', [{ by: 'autocomplete-native', from: 'blurEvent' }]);
				}
			},

			// Keydown handler: only for arrow navigation, enter, esc
			[`keydown.${pluginName}`](/** @type {JQuery.TriggeredEvent} */e) {

				// Make sure slider and whatever else is behind the input don't intercept keys
				e.stopPropagation();
				const key = Number(e.which);
				const { options } = that;
				const { $sgCont } = that.refs;
				const sgContElement = $sgCont[0];
				let $sel;

				switch (key) {

					// case 9: // TAB
					// case 33: // PG UP
					// case 34: // PG DOWN
					case 40: // DOWN
					case 38: { // UP

						// Disabled because tab is not used anymore here
						// if ((key == 9) && (this._sgIsHidden === true)) {
						// 	// Do nothing
						// 	return void 0;
						// }

						// && sgContElement.innerHTML

						let $next;
						const $all = $sgCont.find(itemSelector);
						const $sel = $all.filter(itemIsSelectedSelector);
						const len = $all.length;

						// Custom getIndex function because $().index() is not working as expected
						const getIndex = ($elem) => {
							const elem = $elem[0];
							for (let index = 0; index < len; ++index) {
								if ($all[index] === elem) {
									return index;
								}
							}
							return -1;
						};

						// const dir = ((key == 40) || (key == 34) || ((key == 9) && !e.shiftKey)) ? 'down' : 'up';
						const dir = (key === 40) ? 'down' : 'up';

						// No active selection
						if ($sel.length === 0) {

							// Find the first selectable element
							// Direction: down
							if (dir === 'down') {

								// Page down
								// eslint-disable-next-line unicorn/prefer-ternary, no-constant-condition
								if (/* key_34 */ false) {
									// First element in list
									$next = $sgCont.find(itemSelector).first();
								}
								// Arrow down
								else {
									// First element in list
									$next = $all.first();
								}
							}
							// Direction: up
							else {
								// Page up
								// eslint-disable-next-line unicorn/prefer-ternary, no-constant-condition
								if (/* key == 33 */ false) {
									// Last element in list
									$next = $sgCont.find(itemSelector).last();
								}
								// Arrow up
								else {
									// Last element in list
									$next = $all.last();
								}
							}

							if ($next.length > 0) {
								$next.addClass(options.sgClassSelected).attr('data-search-results-item-selected', '');
								$input.val($next.data('text')).trigger(`input.${pluginName}`);
							}
						}
						// Got active selection
						else {
							// Direction: down
							if (dir === 'down') {

								// Page down
								// eslint-disable-next-line no-constant-condition
								if (/* key == 34 */ false) {

									// Last element in view
									$next = $all.slice($sel.index(/* itemSelector */) + 1).filter(function() {
										return that.isElementInView(sgContElement, this, true);
									}).last();

									if ($next.length === 0) {

										$next = $all.eq($sel.index(/* itemSelector */) + 1);

										// Have more
										if ($next.length > 0) {

											let h = $next[0].offsetHeight;

											while ($next.length > 0 && (h < sgContElement.offsetHeight)) {
												// TODO: take into account the last element type (separator of item) and add height
												$next = $all.eq($next.index(/* itemSelector */) + 1);

												if ($next.length > 0) {
													h += $next[0].offsetHeight;
												}
											}
										}
									}

									// eslint-disable-next-line unicorn/prefer-ternary
									if ($next.length > 0) {
										$next = $all.eq($sel.index(/* itemSelector */) + 1);
									}
									else {
										$next = $sgCont.find(itemSelector).last();
									}
								}
								// Arrow down
								else {
									$next = $all.eq(getIndex($sel) + 1);
								}
							}
							// Direction: up
							else {

								// Page up
								// eslint-disable-next-line no-constant-condition
								if (/* key == 33 */ false) {
									// Last element in view
									$next = $all.slice(0, $sel.index(/* itemSelector */)).filter(function() {
										return that.isElementInView(sgContElement, this, true);
									}).last();

									if ($next.length > 0) {
										const index = $sel.index(/* itemSelector */) - 1;
										$next = (index >= 0) ? $all.eq(index) : $();
									}
									else {
										$next = $sgCont.find(itemSelector).first();
									}
								}
								// Arrow up
								else {
									const index = getIndex($sel) - 1;
									$next = (index >= 0) ? $all.eq(index) : $();
								}
							}

							// -- Found item --
							if ($next.length > 0) {
								$sel.removeClass(options.sgClassSelected).removeAttr('data-search-results-item-selected');
								$next.addClass(options.sgClassSelected).attr('data-search-results-item-selected', '');
								$input.val($next.data('text')).trigger(`input.${pluginName}`);
							}
							// -- No item -- (Reached the top/bottom of the list)
							else {
								// Only if there were selectable items to navigate on
								if ($sgCont.find(itemSelector).length > 0) {
									$input.val(that.lastValue).trigger(`input.${pluginName}`);
								}
								$sel.removeClass(options.sgClassSelected).removeAttr('data-search-results-item-selected');
								$next = null;
							}
						}

						that.sgShow();
						that.sgSetScrollTop($next, dir);
						return false;
					}

					case 27: { // ESC

						$input.val(that.lastValue).trigger(`input.${pluginName}`);

						// Results are already hidden
						if ((that._sgIsHidden === true) || (options.closeBlur === true)) {

							// Hide the results
							if (!that._sgIsHidden) {
								that.sgHide();
							}

							// Put the focus on the first :focusable element
							// $('button, a, input, select, textarea, [tabindex]:not([tabindex="-1"]').filter(':visible').eq(0).trigger('focus');
							// Put the focus on the body
							$(document.body).attr('tabindex', -1).trigger('focus').removeAttr('tabindex');
						}
						else {
							that.sgHide();
						}
						// $input.trigger('blur.' + pluginName);

						break;
					}

					case 13: { // ENTER

						const val = inputElement.value;
						// Get the currently selected search result
						$sel = $sgCont.find(itemIsSelectedSelector);

						// Select first suggestion
						if ($sel.length === 0 && !that._sgIsHidden && options.selectFirst) {
							$sel = $sgCont.find(itemSelector).first();
						}

						// Valid selection
						if ($sel.length > 0) {
							if (!that._sgIsHidden) {
								// Select the item by ref
								that.selectItem($sel, {}, e);
							}
						}
						// Allow discarding values
						else if (!val || (val.length < options.minChars)) {
							const inputData = $input.data();
							if (inputData?.autocomplete?.text !== val) {
								e.preventDefault();
							}
							// Abort pending queries
							that._queryAbort();
							// Make the selection
							that._triggerSelectItemChange(val, { id: null, text: val }, { hideSuggestions: true });
						}

						break;
					}

					case 37: // LEFT
					case 39: { // RIGHT
						// do nothing

						break;
					}
					// No default
				}

				// console.info('keydown (' + e.which + ')', 'input value: ', inputElement.value)
				return void 0;
			},

			// Change/Autocomplete handlers: for outside changes
			// @ts-ignore Not all code paths return a value.ts(7030)
			[`change.${pluginName}`](e, args) {

				// console.log('changed (preventChangeOutside = %o) - args: %O, value: %o', this.preventChangeOutside, args, e.currentTarget.value);

				if (that.preventChangeOutside === true) {
					e.stopImmediatePropagation();
					e.stopPropagation();
					e.preventDefault();
					return false;
				}

				// Commit the new changed value
				that.lastChangeValue = inputElement.value;

				// Manual trigger
				if (args && args.by === 'autocomplete-native') {
					// Do nothing here
					// @ts-ignore Not all code paths return a value.ts(7030)
					return;
				}

				// Do an autocomplete
				that.triggerAutocomplete({ displayResults: args && args.displayResults || false, checkAllowInvalid: true });
			},

			// Keyup handler: (simulated input event) for handling the effective search
			[`keyup.${pluginName}`](e) {

				const key = window.event ? e.keyCode : e.which;
				const { options } = that;

				// Run the search on any key press except for those with special functions
				// https://css-tricks.com/snippets/javascript/javascript-keycodes/
				// https://codepen.io/chriscoyier/pen/mPgoYJ
				if (!key || !options.noTriggerSearchKeys[key]) {
					// Do an autocomplete
					that.triggerAutocomplete({ displayResults: true, selectFirst: false });
				}
			},

			// Paste handler
			[`paste.${pluginName}`]() {
				window.clearTimeout(that._timers.pasteSearch);
				that._timers.pasteSearch = window.setTimeout(() => {
					// Do an autocomplete
					that.triggerAutocomplete({ displayResults: true, selectFirst: false });
				}, 50);
			},
		});

		// Attach delegated events to the container
		this.sgAddEvents();

		return this;
	}

	// Remove events from the input element
	removeEvents() {

		// Remove all namespaced events on the input element
		this.refs.$input.off(`.${pluginName}`);
		// Remove all namespaced events on the sgCont element
		this.refs.$sgCont.off(`.${pluginName}`);

		return this;
	}

	// ===========================
	// ===== Private methods =====
	// ===========================

	/**
	 * Normalize the itemData object so it can be used without unexpected errors in dataBinding handlers
	 *
	 * @param {AcItem} item
	 */
	_normalizeItemData(item) {

		let itemData;

		// String
		if (typeof item === 'string') {
			itemData = {
				id: item,
				text: item,
			};
		}
		// Object
		else {
			itemData = item;
		}

		itemData.selectable = (itemData.selectable != null) ? Boolean(itemData.selectable) : true;
		itemData.addClass = itemData.addClass || '';
		itemData.removeClass = itemData.removeClass || '';
		itemData.attr = itemData.attr || {};

		if (itemData.selectable === false) {
			itemData.attr['data-selectable'] = 'false';
		}

		itemData.text = this._stripTags(item.text);

		return itemData;
	}

	/**
	 * Normalize the itemData object in a simple format
	 *
	 * @param  {string|AcItem} item
	 */
	_normalizeItemDataSimple(item) {

		/** @type {AcItem} */
		let itemData;

		// String
		if (typeof item === 'string') {
			itemData = {
				id: item,
				text: item,
			};
		}
		// Object
		else {
			itemData = $.extend({}, item);
		}

		// Remove extraneous fields
		delete itemData.selectable;
		delete itemData.addClass;
		delete itemData.removeClass;
		delete itemData.attr;
		delete itemData.html;

		itemData.text = this._stripTags(itemData.text);

		return itemData;
	}

	/**
	 * Get all text nodes from the element and its children
	 *
	 * @param  {Node} element
	 *
	 * @return {Array<Text>}
	 */
	_getTextNodes(element) {

		let textNodes = [];

		if (!element || !element.childNodes || element.childNodes.length === 0) {
			return textNodes;
		}

		for (const node of element.childNodes) {

			// Text node
			if (node.nodeType === 3) {
				textNodes.push(node);
			}
			// Element node
			else if ((node.nodeType === 1) && node.childNodes.length > 0) {

				// Recursive call
				// eslint-disable-next-line unicorn/prefer-spread
				textNodes = textNodes.concat(this._getTextNodes(node));
			}
		}

		return textNodes;
	}

	/**
	 * Process the started search
	 *
	 * @param  {string} term
	 * @param  {{suggestOnComplete?: boolean}=} opt
	 *
	 * @return {JQueryDeferred|JQueryPromise}
	 */
	_queryStart(term, opt) {

		const that = this;
		// console.warn('Starting query');
		// Use a deferred object
		const dfr = that._newDeferredQueryRemote(term, opt);

		dfr.done((items) => {

			// Call the search complete handler
			that._queryComplete(term, items);

			// Enabled auto suggestions display
			if (opt.suggestOnComplete) {
				that.searchSuggest();
			}

			// Run pending callbacks
			if (that._queueChain.length > 0) {
				while (that._queueChain.length > 0) {
					const pending = that._queueChain.shift();
					pending.fn.apply(that, pending.args);
				}
			}
		});

		return dfr;
	}

	/**
	 * Add callbacks to queue
	 *
	 * @param  {function} fn
	 * @param  {array} args
	 */
	_addQueueChain(fn, args) {

		const that = this;

		// There is an active query delayed
		if (that._currentQueryState === 'delayed') {
			// Add callbacks to internal queue
			that._queueChain.push({ fn, args });
			// console.log('--INT-Queued-- searchSuggest');
		}
		// Query in progress, we can hook directly to it
		else if (that._currentQueryState === 'pending') {
			// Add the callbacks to the promise queue
			that.deferredQuery.done(() => {
				fn.apply(that, args);
			});
			// console.log('-DF-Queued- searchSuggest');
		}
		// No active query running
		else {
			// Just run the function
			fn.apply(that, args);
		}

		return this;
	}

	/**
	 * Process the started search
	 *
	 * @param  {string} term
	 * @param  {any} _opt
	 *
	 * @return {JQueryDeferred}
	 */
	_newDeferredQueryLocal(term, _opt) {

		const that = this;
		const dfr = $.Deferred();

		window.setTimeout(() => {
			term = term.replaceAll(/[$()*+./?[\\\]^{|}\-]/g, '\\$&');
			const re = new RegExp(`(${term.split(' ').join('|')})`, 'gi');
			const items = [];
			let i = 0; const l = that.dataSource.length;
			for (; i < l; ++i) {
				const item = that.dataSource[i];
				const text = that._stripTags((typeof item === 'string') ? item : item.text);
				if (text && re.test(text)) {
					items.push(item);
				}
			}
			dfr.resolve(items);
		});

		return dfr;
	}

	/**
	 * Run an async query
	 *
	 * @param  {string} term
	 * @param  {{url?: string; suggestOnComplete?: boolean;}=} opt
	 *
	 * @return {JQueryDeferred|JQueryPromise}
	 */
	_newDeferredQueryRemote(term, opt) {

		const that = this;
		const { options } = that;
		const ajaxSettings = { ...options.remote };
		let remoteUrl = (opt && opt.url) || ajaxSettings.url || options.url;
		let data = null;

		// Use default _newDeferredQuery if no url provided
		if (remoteUrl == null) {
			return that._newDeferredQueryLocal(term);
		}

		// Prepare url
		if (/{TERM}/.test(remoteUrl)) {
			remoteUrl = remoteUrl.replaceAll('{TERM}', term);
		}
		// Use data from ajax settings
		else if (ajaxSettings.data) {
			data = typeof ajaxSettings.data === 'function' ? ajaxSettings.data(term) : ajaxSettings.data;
		}
		else {
			data = { q: term };
		}

		// Prepare default options for request
		const settings = {
			url: remoteUrl,
			data,
			method: ajaxSettings.method || 'GET',
			dataType: undefined,
			cache: true,
		};

		// Trigger the before-xhr event
		if (that.trigger('before-xhr', [settings]) !== false) {

			const xhr = $.ajax(settings);

			// Add a custom promise handler to transform the response object
			if (options.xhrPromiseHandler) {
				xhr.then(...options.xhrPromiseHandler.call(this, term));
			}

			xhr
				.fail((jqXhr, textStatus, errorThrown) => {
					// Trigger the error event
					if (that.trigger('xhr-error', [jqXhr, textStatus, errorThrown]) !== false && textStatus !== 'abort') {
						console.warn(pluginName, `Ajax failed: [url: ${remoteUrl}]`, jqXhr, textStatus, errorThrown);
					}
				})
				.done((data, textStatus, jqXhr) => {
					// Trigger the success event
					if (that.trigger('xhr-success', [data, textStatus, jqXhr]) !== false) {
						// Placeholder for some future code if need be
					}
				})
				.always((data_jqXhr, textStatus, jqXhr_errorThrown) => {
					// Trigger the xhr-complete event
					if (that.trigger('xhr-complete', [data_jqXhr, textStatus, jqXhr_errorThrown]) !== false) {
						// Placeholder for some future code if need be
					}
				});

			// Trigger the xhr-complete event
			if (that.trigger('xhr-start', [xhr]) !== false) {
				// Placeholder for some future code if need be
			}

			return xhr;
		}

		return $.Deferred().reject(false);
	}

	/**
	 * Abort the current search
	 */
	_queryAbort() {

		const dfr = this.deferredQuery;

		// Abort the current deferred query
		if (this._currentQueryState === 'pending') {
			// console.warn('>> ** Aborted query **');
			// Abort the xhr
			if ('abort' in dfr) {
				// @ts-ignore
				dfr.abort();
			}
			else if (dfr.reject) {
				dfr.reject(false);
			}
		}
		// Clear the current delay
		else if (this._currentQueryState === 'delayed') {
			// console.warn('>> Cleared delay');
			// Clear prev query
			window.clearTimeout(this._timers.searchDelay);
		}

		// Remove all pending callbacks from queue
		if (this._queueChain.length > 0) {
			while (this._queueChain.length > 0) {
				this._queueChain.shift();
			}
		}

		// Clear the pending/delayed state
		this._currentQueryState = null;

		return this;
	}

	/**
	 * Store the search result for different processing
	 *
	 * @param  {string} term
	 * @param  {Array<(string | Object)>} items
	 */
	_queryComplete(term, items) {

		// Reset query state
		this._currentQueryState = null;

		// Set the result
		this.setLastResult(term, items);

		return this;
	}

	/**
	 * Strip tags from text
	 *
	 * @param  {string} html
	 *
	 * @return {string}
	 */
	_stripTags(html) {

		// DO NOT USE THIS as it exposes XSS vulnerabilities
		// eq: this text is executed: <img src=x onerror=console.trace>
		// const div = document.createElement('div');
		// div.innerHTML = html;
		// return div.textContent || div.innerText || '';

		// Use the sanitize method
		return mpro.Sanitize.stripTags(html);
	}

	/**
	 * Highlight the searched term
	 *
	 * @param  {string} text
	 * @param  {RegExp} reSanitize
	 *
	 * @return {string}
	 */
	_highlightItemTerm(text, reSanitize) {
		return mpro.Sanitize.htmlDecode(text).replace(reSanitize, `<span class="${this.options.sgClassHighlight}">$1</span>`);
	}

	/**
	 * Item renderer function
	 *
	 * @param {string} html
	 * @param {RegExp} reSanitize
	 */
	_prepareItemHtml(html, reSanitize) {

		const div = document.createElement('div');
		div.innerHTML = html;

		const textNodes = this._getTextNodes(div);

		for (let i = 0, l = textNodes.length; i < l; ++i) {

			const node = textNodes[i];
			const text = this._highlightItemTerm(node.textContent || '', reSanitize);
			const newHolder = document.createElement('div');
			newHolder.innerHTML = `<span class="${this.options.sgClassText}">${text}</span>`;

			this.replaceNode(node, Array.prototype.slice.call(newHolder.childNodes));
		}

		return div.innerHTML;
	}

	/**
	 * Add the selected item to the input value
	 *
	 * @param  {string} text
	 * @param  {object=} itemData
	 * @param  {{hideSuggestions?: boolean}=} opt
	 */
	_triggerSelectItemChange(text, itemData, opt) {

		const { $input } = this.refs;
		const prevValue = $input[0].value;

		if (typeof text !== 'string') {
			text = String(text);
		}

		opt = opt || {};
		opt.hideSuggestions = (opt.hideSuggestions == null) ? true : Boolean(opt.hideSuggestions);
		itemData = itemData || {};

		// Modify input data ref & value
		$input.data('autocomplete', itemData).val(text).trigger(`input.${pluginName}`);
		this.lastValue = text;

		// Trigger data binding
		this.bindDataExternally(itemData);

		// Hide the suggestions
		if (opt.hideSuggestions === true) {
			this.sgHide();
		}

		// Trigger some input events
		if (prevValue !== text) {
			$input.trigger('change', [{ by: 'autocomplete-native', from: '_triggerSelectItemChange' }]);
		}

		return this;
	}

	// ===========================
	// ===== Static methods ======
	// ===========================

	static defaults = defaults;

	/** @type {Autocomplete[]} */
	static instances = [];
	static version = pluginVersion;
	static namespace = pluginName;
	static throttleTimers = {};

	static create(elem, options) {
		return new this(elem, options);
	}

	static getInstance(elem, options, forceNew) {

		// Get object type
		const type = $.type(elem);

		// By object reference
		if (type === 'object') {

			const inst = $(elem).data(`plugin-${pluginName}`);

			if (inst) {
				return inst;
			}

			/*
			for (var i = Autocomplete.instances.length - 1; i >= 0; ++i) {
				if (Autocomplete.instances[i].refs.$input[0] === elem) {
					return Autocomplete.instances[i];
				}
			}
			*/

			if (forceNew) {
				return new this(elem, options);
			}
		}
		// By array position
		else if (!Number.isNaN(Number(elem))) {
			if (this.instances[elem]) {
				return this.instances[elem];
			}
		}
		// By nth selector
		else if (elem === 'string') {
			// TODO
		}
		// Get last instance
		else {
			const len = this.instances.length;
			if (len) {
				return this.instances[len - 1];
			}
		}

		return null;
	}

	/**
	 * @param {string} action
	 * @param {number} delay
	 */
	static throttleAction(action, delay = 100) {

		const { throttleTimers } = this;

		if (throttleTimers[action]) {
			window.clearTimeout(throttleTimers[action]);
			throttleTimers[action] = null;
		}

		throttleTimers[action] = window.setTimeout(this[action].bind(this), delay);
	}

	static resizeAllSuggestionsContainers() {
		for (const inst of this.instances) {
			inst.sgResize();
		}
	}

	static closeAllSuggestionsContainers() {
		for (const inst of this.instances) {
			inst.sgHide();
		}
	}

	static addDelegates() {

		// Note: adding a throttle to the scroll effect will look weird
		const throttledClose = () => {
			// this.throttleAction('closeAllSuggestionsContainers');
			this.closeAllSuggestionsContainers();
		};

		// Add Window delegates
		$W.on(`resize.${pluginName}`, throttledClose);
		$D.on(`scroll.${pluginName}`, throttledClose);
	}

	static removeDelegates() {
		// Remove Window delegates
		$W.off(`.${pluginName}`);
		$D.off(`.${pluginName}`);
	}
}

// Add autocomplete delegates
Autocomplete.addDelegates();
